import { fetch } from 'cross-fetch';
import {
  NetworkError,
  ValidationError,
  ConfigurationError,
} from './types';

/**
 * Configuration for the Softlock client
 */
export interface SoftlockConfig {
  /** Your Softlock project/tenant ID */
  tenantId: string;
  /** Base URL for your Softlock instance (defaults to official hosted service) */
  baseUrl?: string;
  /** API key for server-side validation (optional for client-side) */
  apiKey?: string;
  /** Cache TTL in milliseconds (default: 5 minutes) */
  cacheTtl?: number;
  /** Enable debug logging */
  debug?: boolean;
}

/**
 * Cache entry for validation results
 */
interface CacheEntry {
  result: any;
  timestamp: number;
  ttl: number;
}

/**
 * Core Softlock client for access key validation
 */
export class SoftlockClient {
  private config: SoftlockConfig;
  private cache: Map<string, CacheEntry> = new Map();

  constructor(config: SoftlockConfig) {
    this.config = {
      baseUrl: 'https://api.softlock.dev',
      cacheTtl: 5 * 60 * 1000, // 5 minutes
      debug: false,
      ...config,
    };

    if (!this.config.tenantId) {
      throw new ConfigurationError('tenantId is required');
    }
  }

  /**
   * Validate an access key
   */
  async validateKey(keyValue: string): Promise<any> {
    if (!keyValue) {
      throw new ValidationError('Key value is required');
    }

    // Check cache first
    const cacheKey = `validate:${keyValue}`;
    const cached = this.cache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      if (this.config.debug) {
        console.log('[SoftlockClient] Returning cached result for key:', keyValue);
      }
      return { ...cached.result, cached: true };
    }

    try {
      const url = `${this.config.baseUrl}/api/validate-key`;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      if (this.config.apiKey) {
        headers['Authorization'] = `Bearer ${this.config.apiKey}`;
      }

      const body = JSON.stringify({
        key: keyValue,
        tenantId: this.config.tenantId,
      });

      if (this.config.debug) {
        console.log('[SoftlockClient] Validating key:', { url, keyValue, tenantId: this.config.tenantId });
      }

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = `HTTP ${response.status}`;

        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorJson.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }

        throw new NetworkError(errorMessage, response.status);
      }

      const result = await response.json();

      if (this.config.debug) {
        console.log('[SoftlockClient] Validation result:', result);
      }

      // Cache the result
      this.cache.set(cacheKey, {
        result,
        timestamp: Date.now(),
        ttl: this.config.cacheTtl!,
      });

      return result;
    } catch (error) {
      if (this.config.debug) {
        console.error('[SoftlockClient] Validation error:', error);
      }

      if (error instanceof Error) {
        throw error;
      }

      throw new NetworkError('Network request failed');
    }
  }

  /**
   * Clear validation cache
   */
  clearCache(): void {
    this.cache.clear();
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<SoftlockConfig>): void {
    this.config = { ...this.config, ...newConfig };
  }

  /**
   * Get current configuration (without sensitive data)
   */
  getConfig(): Omit<SoftlockConfig, 'apiKey'> {
    const { apiKey, ...publicConfig } = this.config;
    return publicConfig;
  }
}

// Default client instance
let defaultClient: SoftlockClient | null = null;

/**
 * Initialize the default Softlock client
 */
export function initSoftlock(config: SoftlockConfig): SoftlockClient {
  defaultClient = new SoftlockClient(config);
  return defaultClient;
}

/**
 * Get the default client instance
 */
export function getSoftlockClient(): SoftlockClient {
  if (!defaultClient) {
    throw new ConfigurationError('Softlock client not initialized. Call initSoftlock() first.');
  }
  return defaultClient;
}

/**
 * Validate a key using the default client
 */
export async function validateKey(keyValue: string): Promise<any> {
  return getSoftlockClient().validateKey(keyValue);
}
